import { useEffect, useState } from 'react';
import { Bot, Camera, Check, CheckCircle2, CornerUpLeft, DollarSign, Download, Eye, FileCheck2, FileText, Image, Mail, Send, Shuffle, Trash2, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatUsd, localeUI } from '../lib/format';
import { uploadFile } from '../lib/upload';
import { FileViewerModal } from './FileViewerModal';
import { FileDrop } from './FileDrop';
import { ClaimEmailBox } from './ClaimEmailBox';
import { ClaimChat } from './ClaimChat';
import { CocGenerateModal, type CocGenerado } from './CocGenerateModal';

/**
 * Módulo COC — panel del claim (tab "COC") y de la fila expandida de la cola
 * Claims → COC. Sube el COC (la IA extrae el monto de completion y lo compara
 * contra el RCV aprobado), guarda el link de fotos, y envía todo al adjuster
 * de la aseguradora del claim (badges COC/Photos: Not Uploaded / Uploaded / Sent).
 */

export type CocState = {
  claim: {
    id: string;
    homeowner_name: string | null;
    claim_number: string | null;
    insurance_company: string | null;
    rcv_amount_cents: number | null;
    /** Contra qué se compara el COC: el ÚLTIMO monto del seguro (ver coc/service.ts). */
    compare_cents: number | null;
    compare_source: 'estimate' | 'claim' | null;
    compare_estimate_date: string | null;
  };
  coc: {
    coc_status: 'not_uploaded' | 'uploaded' | 'sent';
    photos_status: 'not_uploaded' | 'uploaded' | 'sent';
    coc_file_id: string | null;
    coc_file_name: string | null;
    photos_url: string | null;
    extracted_amount_cents: number | null;
    amount_cents: number | null;
    amount_confirmed_at: string | null;
    sent_at: string | null;
    sent_to: string | null;
    sent_by_name: string | null;
    coc_uploaded_at: string | null;
    acknowledged_at: string | null;
    acknowledged_by_name: string | null;
    created_at: string | null;
  };
  send_log: Array<{
    id: string; to_address: string; subject: string | null;
    included_photos: boolean; created_at: string; sent_by: string | null;
  }>;
  recipients: Array<{ email: string; label: string; kind: 'insurance' | 'adjuster' }>;
  claim_files: Array<{ id: string; file_name: string | null; doc_type: string }>;
  photo_files: Array<{ id: string; file_name: string | null; mime_type: string | null; created_at: string }>;
};

/**
 * Los tipos a los que se puede MOVER un archivo que no era el COC. Espejo de la lista blanca del
 * backend (files/service.ts → SELECTABLE_DOC_TYPES) y del desplegable de ClaimFiles, sin 'coc':
 * elegir COC otra vez sería no hacer nada. Si se agrega uno, hay que agregarlo en los tres lados.
 */
const RECLASS_TYPES: Array<{ value: string; key: string }> = [
  { value: 'job_photo', key: 'ft.job_photo' },
  { value: 'estimate', key: 'ft.estimate' },
  { value: 'proactive_estimate', key: 'ft.proactive_estimate' },
  { value: 'mitigation_estimate', key: 'ft.mitigation_estimate' },
  { value: 'rebuild_estimate', key: 'ft.rebuild_estimate' },
  { value: 'estimate_esx', key: 'ft.estimate_esx' },
  { value: 'contract', key: 'ft.contract' },
  { value: 'policy', key: 'ft.policy' },
  { value: 'weather_report', key: 'ft.weather_report' },
  { value: 'roof_diagram', key: 'ft.roof_diagram' },
  { value: 'atr_video', key: 'ft.atr_video' },
  { value: 'invoice', key: 'ft.invoice' },
  { value: 'email_attachment', key: 'ft.email_attachment' },
  { value: 'info_pending', key: 'ft.info_pending' },
  { value: 'other', key: 'ft.other' },
];

const BADGE_CLS: Record<string, string> = { not_uploaded: 'gray', uploaded: 'blue', sent: 'green' };
const STATUS_KEY: Record<string, string> = { not_uploaded: 'coc.stNotUploaded', uploaded: 'coc.stUploaded', sent: 'coc.stSent' };

/** Badge "COC: Uploaded" / "Photos: Sent" — visible sin desplegar (claim y cola). */
export function CocStatusBadge({ kind, status }: { kind: 'coc' | 'photos'; status: string }) {
  const { t } = useI18n();
  return (
    <span className={`badge ${BADGE_CLS[status] ?? 'gray'}`}>
      {t(kind === 'coc' ? 'coc.badgeCoc' : 'coc.badgePhotos')}: {t(STATUS_KEY[status] ?? 'coc.stNotUploaded')}
    </span>
  );
}

const fmtWhen = (iso: string) => new Date(iso).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' });

export function ClaimCocTab({ claimId, onChanged }: { claimId: string; onChanged?: () => void }) {
  const { t } = useI18n();
  const [st, setSt] = useState<CocState | null>(null);
  const [viewFileOpen, setViewFileOpen] = useState(false); // COC en la ventana emergente grande
  const [photoView, setPhotoView] = useState<{ id: string; name: string | null } | null>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [photosDraft, setPhotosDraft] = useState('');
  const [editAmount, setEditAmount] = useState(false);
  const [amountDraft, setAmountDraft] = useState('');
  // "Este archivo no es el COC": tipo elegido + aviso de lo que pasó (el panel se vacía después,
  // así que sin el aviso el usuario no ve confirmación de nada).
  const [reclassType, setReclassType] = useState('');
  const [reclassing, setReclassing] = useState(false);
  const [reclassed, setReclassed] = useState<string | null>(null);
  const [removed, setRemoved] = useState(false);
  // "Generar COC" (dueño 2026-10-28): con o sin monto; la ventana decide monto y build date.
  const [generar, setGenerar] = useState<'amount' | 'none' | null>(null);
  const [generado, setGenerado] = useState<CocGenerado | null>(null);
  const [generadoView, setGeneradoView] = useState(false);

  const apply = (r: CocState) => {
    setSt(r);
    setPhotosDraft(r.coc.photos_url ?? '');
  };
  useEffect(() => {
    let alive = true;
    api.get<CocState>(`/api/claims/${claimId}/coc`).then((r) => { if (alive) apply(r); }).catch(() => {});
    return () => { alive = false; };
  }, [claimId]);

  if (!st) return <div className="page-empty">{t('common.loadingDots')}</div>;
  const { coc, claim } = st;
  const sent = Boolean(coc.sent_at);
  const amount = coc.amount_cents;
  // El ÚLTIMO monto del seguro, no el campo del claim (Manuel 2026-08-06). Con un supplement
  // aprobado el campo del claim se queda corto y el COC salía en rojo por una diferencia que ya
  // se había cerrado. `compare_source` dice de dónde salió, para que el rojo se pueda discutir.
  const rcv = claim.compare_cents;
  const mismatch = amount != null && rcv != null && amount !== rcv;

  const onFile = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true); setErr(null);
    try {
      const key = await uploadFile(f, 'misc');
      apply(await api.post<CocState>(`/api/claims/${claimId}/coc/file`, { fileKey: key, fileName: f.name, mimeType: f.type || null }));
      onChanged?.();
    } catch { setErr(t('coc.sendFailed')); } finally { setUploading(false); }
  };

  const savePhotos = async () => {
    setErr(null);
    try { apply(await api.post<CocState>(`/api/claims/${claimId}/coc/photos`, { url: photosDraft.trim() || null })); onChanged?.(); }
    catch { setErr(t('coc.sendFailed')); }
  };

  const saveAmount = async () => {
    const n = Number(amountDraft);
    if (!Number.isFinite(n) || n < 0) return;
    setErr(null);
    try { apply(await api.post<CocState>(`/api/claims/${claimId}/coc/amount`, { amountCents: Math.round(n * 100) })); setEditAmount(false); onChanged?.(); }
    catch { setErr(t('coc.sendFailed')); }
  };

  // Sube una foto del trabajo (drag & drop o botón). No pasa por la IA: es una foto.
  const onPhoto = async (f: File) => {
    setUploadingPhoto(true); setErr(null);
    try {
      const key = await uploadFile(f, 'misc');
      apply(await api.post<CocState>(`/api/claims/${claimId}/coc/photo-file`, { fileKey: key, fileName: f.name, mimeType: f.type || null }));
      onChanged?.();
    } catch { setErr(t('coc.sendFailed')); } finally { setUploadingPhoto(false); }
  };

  // Varias de golpe: se suben una por una y el estado se refresca al final (las fotos de un
  // trabajo llegan en tanda, arrastrar 20 tiene que ser un solo gesto).
  const onPhotos = async (fs: File[]) => {
    setUploadingPhoto(true); setErr(null);
    try {
      let last: CocState | null = null;
      for (const f of fs) {
        const key = await uploadFile(f, 'misc');
        last = await api.post<CocState>(`/api/claims/${claimId}/coc/photo-file`, { fileKey: key, fileName: f.name, mimeType: f.type || null });
      }
      if (last) apply(last);
      onChanged?.();
    } catch { setErr(t('coc.sendFailed')); } finally { setUploadingPhoto(false); }
  };

  // El archivo enlazado no era el COC: se le pone el tipo correcto y sale de la cola COC.
  const reclassify = async () => {
    if (!reclassType) return;
    setReclassing(true); setErr(null);
    try {
      apply(await api.post<CocState>(`/api/claims/${claimId}/coc/reclassify-file`, { docType: reclassType }));
      setReclassed(t(RECLASS_TYPES.find((d) => d.value === reclassType)?.key ?? 'ft.other'));
      setReclassType('');
      onChanged?.();
    } catch { setErr(t('coc.notCocFailed')); } finally { setReclassing(false); }
  };

  // "Remove file" (Manuel 2026-09-14): el COC subido por error se archiva y sale del COC.
  const removeCoc = async () => {
    if (!window.confirm(t('coc.removeConfirm', { name: coc.coc_file_name ?? 'COC' }))) return;
    setErr(null); setReclassing(true);
    try { apply(await api.del<CocState>(`/api/claims/${claimId}/coc/file`)); setRemoved(true); onChanged?.(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); } finally { setReclassing(false); }
  };
  const removePhoto = async (fileId: string) => {
    setErr(null);
    try { apply(await api.del<CocState>(`/api/claims/${claimId}/coc/photo-file/${fileId}`)); onChanged?.(); }
    catch { setErr(t('coc.sendFailed')); }
  };

  // Paso del tracker: hecho (verde, con detalle) o pendiente (gris).
  const TrackStep = ({ done, icon, label, detail }: { done: boolean; icon: React.ReactNode; label: string; detail?: string | null }) => (
    <span className={`badge ${done ? 'green' : 'gray'}`} style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', opacity: done ? 1 : 0.7 }}>
      {icon} <strong>{label}</strong>{done && detail ? <span style={{ fontWeight: 400 }}>· {detail}</span> : null}
    </span>
  );

  const toggleAck = async () => {
    setErr(null);
    try { apply(await api.post<CocState>(`/api/claims/${claimId}/coc/acknowledge`, { acknowledged: !coc.acknowledged_at })); onChanged?.(); }
    catch { setErr(t('coc.sendFailed')); }
  };

  const onGenerado = (r: CocGenerado) => {
    apply(r.state);
    setGenerado(r);
    setGenerar(null);
    setRemoved(false); setReclassed(null);
    // Se abre en grande para verlo: es lo que se le va a mandar al seguro.
    setGeneradoView(true);
    onChanged?.();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* GENERAR COC — lo primero de la pestaña (dueño 2026-10-28): el seguro lo pide y a veces el contratista
          no lo manda. Con monto (el más alto del seguro, el nuestro o uno escrito) o sin monto. */}
      <div className="card" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <div className="card-title" style={{ fontSize: 'var(--fs-sm)' }}>
            <FileCheck2 size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.genTitle')}
          </div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{t('coc.genHelp')}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button className="btn sm" onClick={() => setGenerar('amount')}>
            <DollarSign size={14} strokeWidth={1.75} /> {t('coc.genWithAmount')}
          </button>
          <button className="btn ghost sm" onClick={() => setGenerar('none')}>
            <FileText size={14} strokeWidth={1.75} /> {t('coc.genWithoutAmount')}
          </button>
        </div>
        {generado && (
          <div style={{ flexBasis: '100%', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span className="badge green" style={{ whiteSpace: 'normal' }}>
              <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />{' '}
              {t(generado.linked ? 'coc.genDone' : 'coc.genDoneFilesOnly', { name: generado.file.file_name })}
            </span>
            <button className="btn ghost sm" onClick={() => setGeneradoView(true)}><Eye size={14} strokeWidth={1.75} /> {t('coc.viewFile')}</button>
          </div>
        )}
      </div>
      {generar && (
        <CocGenerateModal claimId={claimId} withAmount={generar === 'amount'} onClose={() => setGenerar(null)} onGenerated={onGenerado} />
      )}
      {generadoView && generado && (
        <FileViewerModal fileId={generado.file.id} title={generado.file.file_name} onClose={() => setGeneradoView(false)} />
      )}

      {/* TRACKER estilo contrato: Recibido → Enviado (quién + a qué email) → Acknowledged. */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', border: '1px solid var(--line)', borderRadius: 10, padding: '8px 10px' }}>
        <TrackStep done={Boolean(coc.coc_file_id)} icon={<Download size={12} strokeWidth={1.75} />} label={t('coc.trkReceived')}
          detail={coc.coc_uploaded_at ? fmtWhen(coc.coc_uploaded_at) : null} />
        <span className="muted">→</span>
        <TrackStep done={Boolean(coc.sent_at)} icon={<Send size={12} strokeWidth={1.75} />} label={t('coc.trkSent')}
          detail={coc.sent_at ? `${fmtWhen(coc.sent_at)}${coc.sent_by_name ? ` · ${coc.sent_by_name}` : ''}${coc.sent_to ? ` · → ${coc.sent_to}` : ''}` : null} />
        <span className="muted">→</span>
        <TrackStep done={Boolean(coc.acknowledged_at)} icon={<CheckCircle2 size={12} strokeWidth={1.75} />} label={t('coc.trkAck')}
          detail={coc.acknowledged_at ? `${fmtWhen(coc.acknowledged_at)}${coc.acknowledged_by_name ? ` · ${t('coc.trkAckBy')} ${coc.acknowledged_by_name}` : ''}` : null} />
        <span style={{ flex: 1 }} />
        {/* Marca manual: el staff lo pone cuando el seguro confirma que recibió el COC. */}
        {coc.acknowledged_at
          ? <button className="btn ghost sm" style={{ fontSize: 'var(--fs-2xs)' }} onClick={toggleAck}><CornerUpLeft size={12} strokeWidth={1.75} /> {t('coc.trkUnack')}</button>
          : sent && <button className="btn sm" style={{ fontSize: 'var(--fs-2xs)', background: 'var(--good-fill)', color: 'var(--good-fill-ink)', borderColor: 'transparent' }} onClick={toggleAck}><CheckCircle2 size={12} strokeWidth={1.75} /> {t('coc.trkMarkAck')}</button>}
      </div>
      {/* Badges de estatus — visibles arriba, sin desplegar nada. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <CocStatusBadge kind="coc" status={coc.coc_status} />
        <CocStatusBadge kind="photos" status={coc.photos_status} />
        {sent && coc.sent_at && (
          <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {t('coc.sentOn', { when: fmtWhen(coc.sent_at) })}{coc.sent_to ? ` ${t('coc.sentTo', { to: coc.sent_to })}` : ''}
          </span>
        )}
      </div>
      {err && <div className="badge red" style={{ alignSelf: 'flex-start' }}>{err}</div>}

      {/* COC file + link de fotos: independientes (uno, el otro, o ambos). */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: 12 }}>
        <div className="card" style={{ margin: 0 }}>
          <div className="card-title" style={{ fontSize: 'var(--fs-sm)' }}><FileText size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.uploadTitle')}</div>
          {/* Drag & drop (Manuel 2026-08-06). Era el único upload del módulo que seguía siendo un
              botón: las fotos de acá al lado ya se arrastraban, y el COC —el archivo que de
              verdad cierra el claim— obligaba a abrir el explorador. Mismo FileDrop, para que
              las dos mitades de la tarjeta se usen igual. */}
          <div style={{ marginTop: 8 }}>
            <FileDrop
              onFile={onFile}
              accept="application/pdf,image/*"
              label={`${t('coc.uploadBtn')}`}
              busy={uploading}
              busyLabel={t('coc.uploading')}
              compact
            />
          </div>
          {coc.coc_file_id && (
            <div style={{ marginTop: 8 }}>
              <button className="btn ghost sm" onClick={() => setViewFileOpen(true)}><Eye size={14} strokeWidth={1.75} /> {t('coc.viewFile')}{coc.coc_file_name ? ` — ${coc.coc_file_name}` : ''}</button>
              {!sent && (
                <button className="btn ghost sm" onClick={removeCoc} disabled={reclassing} title={t('coc.removeHelp')} style={{ marginLeft: 6, color: 'var(--red)' }}>
                  <Trash2 size={14} strokeWidth={1.75} /> {t('coc.removeFile')}
                </button>
              )}
            </div>
          )}
          {/* El COC se abre en GRANDE, centrado sobre la pantalla (antes era un iframe chiquito
              dentro de esta tarjeta y no se podía leer). */}
          {viewFileOpen && coc.coc_file_id && (
            <FileViewerModal fileId={coc.coc_file_id} title={coc.coc_file_name || 'COC'} onClose={() => setViewFileOpen(false)} />
          )}
          {/* "No es el COC" (Manuel 2026-08-07): el contratista sube cualquier cosa y el claim se
              queda en la cola con su tarea. Acá se le pone el tipo REAL y sale de COC. No se
              ofrece si ya se envió: eso es historia, no un error de tipeo. */}
          {coc.coc_file_id && !sent && (
            <div style={{ marginTop: 10, borderTop: '1px dashed var(--line)', paddingTop: 8 }}>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)' }}><Shuffle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.notCocTitle')}</div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{t('coc.notCocHelp')}</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
                <select
                  className="input sm"
                  style={{ flex: 1, minWidth: 170 }}
                  value={reclassType}
                  onChange={(e) => setReclassType(e.target.value)}
                >
                  <option value="">{t('coc.notCocPick')}</option>
                  {RECLASS_TYPES.map((d) => <option key={d.value} value={d.value}>{t(d.key)}</option>)}
                </select>
                <button className="btn sm" onClick={reclassify} disabled={!reclassType || reclassing}>
                  {reclassing ? t('coc.uploading') : t('coc.notCocBtn')}
                </button>
              </div>
            </div>
          )}
          {reclassed && (
            <div className="badge green" style={{ marginTop: 8, display: 'inline-block' }}>
              <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.notCocDone', { type: reclassed })}
            </div>
          )}
          {removed && (
            <div className="badge green" style={{ marginTop: 8, display: 'inline-block' }}>
              <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.removedDone')}
            </div>
          )}
        </div>
        <div className="card" style={{ margin: 0 }}>
          <div className="card-title" style={{ fontSize: 'var(--fs-sm)' }}><Camera size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.photosTitle')}</div>
          {/* Dos caminos para lo mismo: el LINK (Drive/Dropbox/CompanyCam) o subir las fotos acá. */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
            <input className="input sm" style={{ flex: 1, minWidth: 180 }} placeholder={t('coc.photosPlaceholder')} value={photosDraft} onChange={(e) => setPhotosDraft(e.target.value)} />
            <button className="btn sm" onClick={savePhotos} disabled={photosDraft.trim() === (coc.photos_url ?? '')}>{t('coc.photosSave')}</button>
            {coc.photos_url && <a className="btn ghost sm" href={coc.photos_url} target="_blank" rel="noreferrer">↗ {t('coc.photosOpen')}</a>}
          </div>
          <div style={{ marginTop: 8 }}>
            <FileDrop
              onFile={onPhoto}
              onFiles={onPhotos}
              multiple
              accept="image/*,application/pdf"
              label={`${t('coc.photosUpload')}`}
              busy={uploadingPhoto}
              busyLabel={t('coc.uploading')}
              compact
            />
          </div>
          {st.photo_files.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
              {st.photo_files.map((f) => (
                <span key={f.id} className="badge" style={{ fontSize: 'var(--fs-2xs)', display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--surface-2)' }}>
                  <button
                    onClick={() => setPhotoView({ id: f.id, name: f.file_name })}
                    style={{ border: 0, background: 'none', cursor: 'pointer', color: 'inherit', padding: 0, font: 'inherit' }}
                  >
                    <Image size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {f.file_name || f.id}
                  </button>
                  <button onClick={() => removePhoto(f.id)} style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}><X size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /></button>
                </span>
              ))}
            </div>
          )}
          {photoView && (
            <FileViewerModal fileId={photoView.id} title={photoView.name} onClose={() => setPhotoView(null)} />
          )}
        </div>
      </div>

      {/* Comparación IA: monto del COC vs RCV aprobado, con confirmación humana. */}
      {(coc.coc_file_id || amount != null) && (
        <div className="card" style={{ margin: 0, borderLeft: mismatch ? '3px solid var(--red)' : undefined }}>
          <div className="card-title" style={{ fontSize: 'var(--fs-sm)' }}><Bot size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.aiTitle')}</div>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{t('coc.aiHelp')}</div>
          <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('coc.cocAmount')}</div>
              {editAmount ? (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input className="input sm" type="number" min={0} step="0.01" style={{ width: 130 }} value={amountDraft} onChange={(e) => setAmountDraft(e.target.value)} />
                  <button className="btn sm" onClick={saveAmount}>{t('coc.saveBtn')}</button>
                  <button className="btn ghost sm" onClick={() => setEditAmount(false)}>{t('coc.cancelBtn')}</button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
                  <strong style={{ fontSize: 'var(--fs-md)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{amount != null ? formatUsd(amount) : '—'}</strong>
                  <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                    {coc.amount_confirmed_at ? t('coc.confirmed') : amount != null ? t('coc.extracted') : ''}
                  </span>
                  <button className="btn ghost sm" onClick={() => { setAmountDraft(amount != null ? String(amount / 100) : ''); setEditAmount(true); }}>{t('coc.editBtn')}</button>
                  {amount != null && !coc.amount_confirmed_at && (
                    <button
                      className="btn sm"
                      onClick={async () => {
                        try { apply(await api.post<CocState>(`/api/claims/${claimId}/coc/amount`, { amountCents: amount })); onChanged?.(); }
                        catch { setErr(t('coc.sendFailed')); }
                      }}
                    >
                      {t('coc.confirmBtn')}
                    </button>
                  )}
                </div>
              )}
            </div>
            <div>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {claim.compare_source === 'estimate' ? t('coc.latestInsurance') : t('coc.rcvApproved')}
              </div>
              <strong style={{ fontSize: 'var(--fs-md)', fontVariantNumeric: 'tabular-nums', fontFamily: 'var(--font-mono)' }}>{rcv != null ? formatUsd(rcv) : '—'}</strong>
              {/* De dónde salió el número: un rojo sin procedencia no se puede discutir. */}
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                {claim.compare_source === 'estimate'
                  ? `${t('coc.fromInsEstimate')}${claim.compare_estimate_date ? ` · ${claim.compare_estimate_date}` : ''}`
                  : claim.compare_source === 'claim'
                    ? t('coc.fromClaimField')
                    : ''}
              </div>
            </div>
            <div>
              {amount == null ? (
                <span className="badge amber">{t('coc.amountMissing')}</span>
              ) : mismatch ? (
                <span className="badge red">{t('coc.amountMismatch')}</span>
              ) : rcv != null ? (
                <span className="badge green">{t('coc.amountMatch')}</span>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Envío al adjuster — el MÓDULO DE EMAIL de siempre (el mismo de todas las vistas), con
          las plantillas de la vista COC y la copia al homeowner/contratista. Al enviar se marca
          el COC como Sent y el claim pasa a Payments (Waiting Depreciation). A la derecha, el
          follow-up del departamento: ese hueco estaba muerto. */}
      <div className="card" style={{ margin: 0 }}>
        <div className="card-title" style={{ fontSize: 'var(--fs-sm)' }}><Mail size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('coc.sendTitle')}</div>
        {sent && coc.sent_at && (
          <div style={{ margin: '8px 0' }}>
            <span className="badge green">{t('coc.sentOn', { when: fmtWhen(coc.sent_at) })}</span>
            {coc.sent_to && <span className="muted" style={{ marginLeft: 8, fontSize: 'var(--fs-sm)' }}>{t('coc.sentTo', { to: coc.sent_to })}</span>}
          </div>
        )}
        <div style={{ marginTop: 8 }}>
          <ClaimEmailBox
            claimId={claimId}
            claimNumber={claim.claim_number}
            departmentKey="claims"
            defaultTab="insurance"
            defaultTo={{ insurance: st.recipients[0]?.email ?? null }}
            templatesScope={{ deptKey: 'claims', viewKey: 'COC' }}
            // El link de fotos se mete en el mensaje con un click (antes se pegaba solo al final;
            // así el que escribe decide dónde va).
            extraChips={coc.photos_url ? [{
              label: t('coc.photosChip'),
              value: `<p>Job photos: <a href="${coc.photos_url.replace(/"/g, '%22')}">${coc.photos_url.replace(/</g, '&lt;')}</a></p>`,
            }] : []}
            // El COC y las fotos subidas se adjuntan solos: es lo que hay que mandar.
            preAttachFileIds={[
              ...(coc.coc_file_id ? [coc.coc_file_id] : []),
              ...st.photo_files.map((f) => f.id),
            ]}
            onSent={async () => {
              try { apply(await api.post<CocState>(`/api/claims/${claimId}/coc/mark-sent`, {})); onChanged?.(); }
              catch { /* el email SÍ salió; si falla marcarlo, el badge se queda como estaba */ }
            }}
            side={<ClaimChat claimId={claimId} channel="claims" title={t('coc.deptFollowUp')} />}
          />
        </div>

        {/* Historial de envíos. */}
        <div style={{ marginTop: 12 }}>
          <div className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)' }}>{t('coc.historyTitle')}</div>
          {st.send_log.length === 0 ? (
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 4 }}>{t('coc.historyNone')}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
              {st.send_log.map((l) => (
                <div key={l.id} style={{ fontSize: 'var(--fs-sm)' }}>
                  <Mail size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {fmtWhen(l.created_at)} → <strong>{l.to_address}</strong>
                  {l.subject ? <span className="muted"> — {l.subject}</span> : null}
                  {l.included_photos ? <span className="badge blue" style={{ marginLeft: 6 }}><Camera size={12} strokeWidth={1.75} /> {t('coc.historyPhotos')}</span> : null}
                  {l.sent_by ? <span className="muted"> · {l.sent_by}</span> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
